<template>
  <SignInSignUpLayout>
    <b-form
      @submit="completeSignup"
      class="sing-in-form"
    >
      <h1 class="sign-in-title">Complete Sign Up</h1>

      <Description>
        To get started, please let us know your name and create a secure password to set up your Easy Agency administrator account.
      </Description>

      <UserNameField
        ref="userNameComponent"
        :serverErrors="nameServerErrors"
        :hideValidationErrors="!submitButtonPressed"
      />

      <PasswordField
        v-model="password"
        id="password"
        label="Password"
        placeholder="Enter password"
        autocomplete="new-password"
        :state="passwordState"
      >
        <template #invalid-feedback>
          <div v-for="(error, index) in passwordServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </PasswordField>

      <PasswordRequirements
        :hideStatuses="!submitButtonPressed"
        :hasMinLength="passwordValidationStatus.hasMinLength"
        :hasUppercaseAndLowercase="passwordValidationStatus.hasUppercase && passwordValidationStatus.hasLowercase"
        :hasNumbers="passwordValidationStatus.hasNumbers"
        :hasSpecialChars="passwordValidationStatus.hasSpecialChars"
      />

      <b-button
        class="button confirm-button"
        type="submit"
        variant="primary"
        :disabled="isSubmitButtonDisabled"
      >
        Confirm
      </b-button>

      <CompleteSignupProgressRegular
        :currentStep="1"
      />
    </b-form>
  </SignInSignUpLayout>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { validatePassword } from '@components/sign-up/validatePassword'
import { COMPLETE_SIGN_UP_ADD_AGENCY_STEP, COMPLETE_SIGN_UP_STEP } from '@/constants/localStorageKeys'
import { isConfirmTokenPresent, showSessionExpiredWarning } from '@utils/confirmationToken'
import SignInSignUpLayout from '@components/sign-up/SignInSignUpLayout.vue'
import UserNameField from '@components/sign-up/complete-signup-progress/UserNameField.vue'
import PasswordField from '@components/sign-up/PasswordField.vue'
import PasswordRequirements from '@components/sign-up/PasswordRequirements.vue'
import CompleteSignupProgressRegular from '@components/sign-up/complete-signup-progress/CompleteSignupProgressRegular.vue'
import Description from '@components/sign-up/Description.vue'

export default {
  name: 'CompleteSignUp',
  mixins: [ validationMixin ],
  components: { SignInSignUpLayout, UserNameField, PasswordField, PasswordRequirements, CompleteSignupProgressRegular, Description },
  data () {
    return {
      password: '',

      nameServerErrors: null,
      passwordServerErrors: null,

      submitButtonPressed: false,
      submitRequestPending: false
    }
  },
  validations: {
    password: {
      validPassword (password) {
        return validatePassword(password).isValid
      }
    }
  },
  async created () {
    if (localStorage.getItem(COMPLETE_SIGN_UP_STEP) === COMPLETE_SIGN_UP_ADD_AGENCY_STEP) {
      this.$router.replace({ name: this.ROUTES.ADD_AGENCY.name })
    }
  },
  mounted () {
    // Show warning message on page load if no confirmation cookie
    if (!isConfirmTokenPresent(this.$cookies)) {
      showSessionExpiredWarning(this)
    }
  },
  computed: {
    passwordState () {
      if (!this.passwordServerErrors) {
        return null
      }

      return this.passwordServerErrors || this.$v.password.$invalid ? false : null
    },
    passwordValidationStatus () {
      return validatePassword(this.password)
    },
    isFormValid () {
      return !this.$v.$invalid && !this.$refs.userNameComponent.$v.$invalid
    },
    isSubmitButtonDisabled () {
      return this.submitRequestPending || (this.submitButtonPressed && !this.isFormValid)
    }
  },
  methods: {
    touchForm () {
      this.$v.$touch()
      this.$refs.userNameComponent.$v.$touch()
    },
    completeSignup (e) {
      e.preventDefault()

      this.submitButtonPressed = true
      this.nameServerErrors = null
      this.passwordServerErrors = null

      this.touchForm()
      if (!this.isFormValid) {
        return
      }

      this.submitRequestPending = true

      this.axios
        .post('/api/register/complete-signup', {
          name: this.$refs.userNameComponent.name,
          password: this.password
        })
        .then(() => {
          localStorage.setItem(COMPLETE_SIGN_UP_STEP, COMPLETE_SIGN_UP_ADD_AGENCY_STEP)
          // localStorage.setItem('agency_prefix', true)
          localStorage.removeItem('tenant_sign_up')
          this.$router.replace({ name: this.ROUTES.ADD_AGENCY.name })
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            const { name, password, ...restErrors } = error.response.data.errors

            if (name) this.nameServerErrors = name
            if (password) this.passwordServerErrors = password

            const errors = Object.values(restErrors)
            for (let i = 0; i < errors.length; i++) {
              this.$snotify.error(
                errors[i][0],
                'Error',
                this.$store.state.snotify
              )
            }
          } else if (error) {
            this.$snotify.error(
              error,
              'Error',
              this.$store.state.snotify
            )
          }
        })
        .finally(() => {
          this.submitRequestPending = false
        })
    }
  }
}
</script>
